import { useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { NotePencilIcon } from "@phosphor-icons/react";

/** Solo el Administrador General asigna o cambia tarifas (P1–P4) y autoriza P0 en proformas, con bitácora. */
export const puedeCambiarTarifa = (user) =>
  user?.role === "admin" || Boolean(user?.effective?.is_admin);

/** Bitácora de tarifas del documento (proforma o la factura que la heredó): quién, cuándo y qué cambió. */
export function BitacoraTarifas({ items = [] }) {
  if (!items || !items.length) return null;
  return (
    <div className="border border-agro-yellow/30 bg-agro-yellow/5 rounded-md p-3 mt-4" data-testid="bitacora-tarifas">
      <div className="text-[10px] uppercase tracking-wider text-agro-yellow font-semibold mb-1.5 flex items-center gap-1">
        <NotePencilIcon size={12} /> Notas de tarifa de precios ({items.length})
      </div>
      <ul className="space-y-1 text-xs">
        {items.map((b, i) => (
          <li key={i} className="text-text-secondary">
            <span className="text-text-primary font-medium">{b.accion}</span> · {b.nota}
            <span className="text-text-muted"> · {fmtDateTime(b.fecha)}{b.usuario && b.usuario !== b.usuario_nombre ? ` (${b.usuario})` : ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const NIVELES = ["p1", "p2", "p3", "p4", "p0"];

/** Ventana para cambiar la tarifa de las líneas de una proforma (antes de facturarla). */
export function TarifasModal({ p, onClose, onSaved }) {
  const [lineas, setLineas] = useState((p.items || []).map((it) => ({
    nivel: (it.nivel_aplicado || "").toLowerCase(), precio: it.precio_unitario, original: (it.nivel_aplicado || "").toLowerCase(),
    precioOriginal: it.precio_unitario,
  })));
  const [motivo, setMotivo] = useState("");
  const cambios = lineas.map((l, i) => ({ ...l, index: i }))
    .filter((l) => l.nivel !== l.original || (l.nivel === "p0" && Number(l.precio) !== Number(l.precioOriginal)));
  const save = async () => {
    if (!cambios.length) { toast.error("No cambiaste ninguna tarifa"); return; }
    if (cambios.some((c) => c.nivel === "p0" && !(Number(c.precio) > 0))) { toast.error("Indica el precio P0 autorizado"); return; }
    try {
      const { data } = await api.post(`/sales/proformas/${p.id}/tarifas`, {
        motivo, lineas: cambios.map((c) => ({ index: c.index, nivel: c.nivel, precio_unitario: c.nivel === "p0" ? Number(c.precio) : null })),
      });
      toast.success("Tarifa actualizada y registrada en la bitácora");
      onSaved(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: { lineas, motivo }, onClose, onSave: save,
                          mensaje: "¿Está seguro de que desea aplicar el cambio de tarifa a esta proforma?" });
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tarifas-modal">
        <div className="p-4 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Tarifa de precios · {p.numero}</div>
            <h3 className="font-heading text-lg">Cambiar tarifa o autorizar P0</h3>
            <div className="text-xs text-text-muted">Cliente {p.cliente_nombre} · tarifa del cliente {(p.nivel_precio_cliente || "—").toUpperCase()}.
              El precio de P1–P4 es el de la tarifa del producto; P0 lleva el precio autorizado.</div>
          </div>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-text-muted text-left">
              <tr><th className="p-2">Producto</th><th className="p-2 text-right">Cant.</th><th className="p-2">Tarifa</th><th className="p-2 text-right">Precio</th></tr>
            </thead>
            <tbody>
              {(p.items || []).map((it, i) => {
                const l = lineas[i];
                const cambiada = l.nivel !== l.original;
                return (
                  <tr key={i} className={`border-t border-border/30 ${cambiada ? "bg-agro-yellow/5" : ""}`}>
                    <td className="p-2 text-xs">{it.codigo} — {it.descripcion}</td>
                    <td className="p-2 text-right">{it.cantidad}</td>
                    <td className="p-2">
                      <select value={l.nivel} data-testid={`tarifa-nivel-${i}`}
                        onChange={(e) => setLineas(lineas.map((x, j) => (j === i ? { ...x, nivel: e.target.value } : x)))}
                        className="bg-app border border-border rounded px-2 py-1 text-xs">
                        {NIVELES.map((n) => <option key={n} value={n}>{n.toUpperCase()}{n === "p0" ? " (autorizar)" : ""}</option>)}
                      </select>
                      {cambiada && <div className="text-[10px] text-agro-yellow mt-0.5">antes {l.original.toUpperCase()}</div>}
                    </td>
                    <td className="p-2 text-right">
                      {l.nivel === "p0" ? (
                        <input type="number" min="0" step="0.01" value={l.precio} data-testid={`tarifa-precio-${i}`}
                          onChange={(e) => setLineas(lineas.map((x, j) => (j === i ? { ...x, precio: e.target.value } : x)))}
                          className="w-28 bg-app border border-agro-yellow/50 rounded px-2 py-1 text-xs text-right" />
                      ) : <span className="text-xs text-text-muted">{cambiada ? "precio de la tarifa" : fmtL(it.precio_unitario)}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted mt-4 mb-1">Motivo (queda en la bitácora)</label>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="tarifa-motivo"
            placeholder="Ej.: volumen de compra, negociación aprobada por gerencia…" className="w-full bg-app border border-border rounded px-3 py-2 text-sm" />
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving || !cambios.length} data-testid="tarifa-guardar"
            className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">
            {m.saving ? "Guardando…" : `Aplicar ${cambios.length || ""} cambio(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
